import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { filter, map, mergeMap, Observable, of } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState } from '../../../store/app.selectors';

import { skipMessagingKey } from '../../../core/services/http.context';
import { MessagingEnvironmentService } from '../../../core/services/messaging-environment.service';
import { IChannel } from '../interfaces/IChannel';
import { DealerSettingName } from '../interfaces/IDealerSettings';
import { IMessage, Suggestion } from '../interfaces/IMessage';
import { WorkspaceFeatures } from '../interfaces/IWorkspace';
import { AppStateWithChat } from '../store/chat.reducer';
import { LogActions, LogModules, LogsService } from './logs.service';
import { PermissionsService } from './permissions.service';
import { TranslaterService } from './translater.service';

@Injectable()
export class SmartRepliesService {
  messages: IMessage[] = [];
  smartRepliesEnabled: boolean;
  selectedChannel: IChannel | null = null;

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppStateWithChat>,
    private permissionsService: PermissionsService,
    private translaterService: TranslaterService,
    private env: MessagingEnvironmentService,
    private logsService: LogsService
  ) {
    this.setChatSubscription();
  }

  setChatSubscription(): void {
    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ smartRepliesEnabled, selectedChannel }) => {
        this.smartRepliesEnabled = smartRepliesEnabled;
        this.selectedChannel = selectedChannel;
      });
  }

  getSuggestions(channel: IChannel): Observable<Suggestion[]> {
    if (!this.canUseSmartReplies || !channel.enabled) {
      return of([]);
    }
    const message = this.getMessage(channel.messages);
    if (!message) {
      return of([]);
    }
    if (channel.smartReplies?.length) {
      return of(channel.smartReplies);
    }
    if (this.translaterService.verifyCanTranslate) {
      return this.translaterService.translateText(message, this.translaterService.dealerLanguge).pipe(
        mergeMap((res) => {
          return this.getSuggestionsByMessage(res);
        })
      );
    } else {
      return this.getSuggestionsByMessage(message);
    }
  }

  getSuggestionsByMessage(messages: string): Observable<Suggestion[]> {
    this.logsService.sendLog(LogModules.OPENASSISTANT, LogActions.CLICK).subscribe();
    return this.httpClient
      .post<Suggestion[]>(
        `${this.env.env.textProcessorApiUrl}/smart-reply/suggestions`,
        {
          message: messages,
          lang: this.translaterService.dealerLanguge,
        },
        {
          context: skipMessagingKey(),
        }
      )
      .pipe(
        map((suggestions) => {
          return suggestions.filter((suggestion) => suggestion.text);
        })
      );
  }

  setMessages(data: IMessage[]): void {
    this.messages = data;
  }

  getMessage(data: IMessage[]): string | null {
    if (data.length && this.canUseSmartReplies) {
      let lastMessageBody = '';
      let requestToAI = '';
      const messages = data.slice(-2);
      const messagesReverse = messages.reverse();
      if (lastMessageBody !== messages[0]?.body) {
        lastMessageBody = messages[0].body;
        const lastAdvisorMessage = messagesReverse.find((x) => x.outcoming === true);
        if (lastAdvisorMessage) {
          requestToAI = messagesReverse
            .slice(0, messagesReverse.indexOf(lastAdvisorMessage))
            .map((message) => message.body)
            .join(' ');
          if (requestToAI) {
            return requestToAI;
          }
        } else {
          requestToAI = messagesReverse.map((message) => message.body).join(' ');
          if (requestToAI) {
            return requestToAI;
          }
        }
      }
    }
    return null;
  }

  get canUseSmartReplies(): boolean {
    return !!(
      this.selectedChannel?.enabled &&
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseSmartResponses]) &&
      this.permissionsService.checkDealerSetting(DealerSettingName.smartRepliesEnabled) &&
      this.smartRepliesEnabled
    );
  }

  public get showSmartReplies(): boolean {
    return !!(
      this.selectedChannel?.enabled &&
      this.canUseSmartReplies &&
      ((this.selectedChannel?.smartReplies?.length ?? 0) > 0 || this.selectedChannel?.isLoadingSmartReplies)
    );
  }
}
